import { openDB } from 'idb';
import { dehydrate, hydrate, type QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * La caché sobrevive al cierre de la pestaña. El peldaño que nos faltaba.
 *
 * 🚨 EL PROBLEMA. La caché de TanStack Query vive en la memoria de la pestaña: al recargar se borra
 *    entera y el panel arranca de cero. Medido en el panel del founder: la misma lista tarda 947 ms
 *    en frío y 175 ms con caché. Es la MISMA caché — sólo que no sobrevivía a un F5.
 *
 * ⭐ ES LO QUE HIZO SHOPIFY EN 2025. Rehicieron su Admin con prefetch inteligente más caché
 *    persistente en IndexedDB, y lo resumen así: las páginas frecuentes cargan al instante «incluso
 *    entre sesiones». El prefetch ya lo teníamos; esto es la otra mitad.
 *
 * 🪤 SIN DEPENDENCIA NUEVA, Y NO POR AHORRO. El plugin oficial de persistencia no publica una
 *    versión que coincida con nuestro `query-core` (5.90), así que npm instalaba una SEGUNDA copia
 *    del núcleo — dos instancias de `QueryClient` que ni siquiera compilan juntas. `dehydrate` e
 *    `hydrate` ya vienen en la librería que usamos: la persistencia son cuarenta líneas y evita
 *    tener dos núcleos peleando.
 *
 * 🪤 INDEXEDDB Y NO `localStorage`. localStorage tiene ~5 MB y es SÍNCRONO: escribir bloquea el hilo
 *    que pinta, justo lo que se quiere acelerar.
 *
 * 🪤 SE PERSISTE POCO Y A PROPÓSITO. Acá van datos de clientes de un negocio real al disco de quien
 *    usa el panel. Sólo las consultas cuya clave está en la lista blanca, y se BORRA TODO al cerrar
 *    sesión. Guardar «lo que haya» era más fácil y dejaba media base del tenant en una máquina
 *    compartida.
 */

const NOMBRE_BD = 'smarty-cache-consultas';
const ALMACEN = 'tanstack';
const CLAVE = 'panel';
/** Una semana. Son datos que se revalidan solos al abrir; lo más viejo que eso no aporta. */
const VENCE_MS = 7 * 24 * 60 * 60 * 1000;
/** Cada cuánto se escribe a disco como mucho. Escribir en cada tecla del buscador sería absurdo. */
const ESPERA_ESCRITURA_MS = 1_000;

/**
 * Qué consultas vale la pena guardar en disco.
 *
 * ⭐ SÓLO LO QUE SE MIRA AL ABRIR. Esta caché existe para borrar el cargando de la primera pantalla;
 *    persistir lo demás sólo agrega datos en el disco y trabajo de serialización.
 */
/*
 * 🪤 `shalom-tarifa` ESTÁ ACÁ POR UNA RAZÓN MEDIDA: cotizar una ruta nueva son seis viajes a un
 *    Laravel lento. Sin persistir, cada recarga de la pantalla de Entrega volvía a decir
 *    «Calculando…» por algo que ya se sabía. Es exactamente para lo que existe esta lista.
 *
 * 🪤 Y las agencias por el mismo motivo, medido: 565 destinos (97 kB) más los orígenes con sus
 *    listas de destinos aéreos. El catálogo de Shalom cambia muy de vez en cuando — es justo lo que
 *    conviene guardar entre sesiones en vez de volver a bajarlo en cada carga.
 */
/*
 * `ops-equipo` y `logos-de-couriers` (26-sep): los nombres del equipo y los logos de los couriers llegaban
 * después que las filas de Ventas; al recargar, la lista se dibujaba y un segundo después se reacomodaba.
 * Con ellos persistidos, se dibuja una sola vez.
 */
const PERSISTIBLES = [
  'ops-orders', 'ops-orders-total', 'ops-kpis', 'ops-equipo', 'logos-de-couriers', 'canales-del-negocio', 'dashboard',
  'shalom-tarifa', 'shalom-agencias', 'shalom-agencias-origen',
];

interface Guardado {
  /** La versión de la app que lo escribió. Ver `esUtilizable`. */
  version: string;
  guardadoEn: number;
  estado: unknown;
}

function versionDeLaApp(): string {
  return (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev';
}

async function abrir() {
  return openDB(NOMBRE_BD, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(ALMACEN)) db.createObjectStore(ALMACEN);
    },
  });
}

/**
 * ¿Sirve lo que hay en disco?
 *
 * 🪤 LA VERSIÓN IMPORTA TANTO COMO LA FECHA. Si un despliegue cambia la forma de una fila, la caché
 *    vieja pintaría con la forma anterior — un bug que sólo ve quien ya había entrado antes, o sea
 *    el más difícil de reproducir de todos. Al cambiar la versión, lo guardado se descarta.
 */
function esUtilizable(g: Guardado | undefined): g is Guardado {
  if (!g || typeof g !== 'object') return false;
  if (g.version !== versionDeLaApp()) return false;
  return Date.now() - g.guardadoEn < VENCE_MS;
}

/** Borra lo guardado. La llama el cierre de sesión: los datos de un negocio no sobreviven al logout. */
export async function olvidarCachePersistida(): Promise<void> {
  try {
    const bd = await abrir();
    await bd.delete(ALMACEN, CLAVE);
  } catch { /* si no se puede abrir, no hay nada que borrar */ }
}

/**
 * Enciende la persistencia: restaura lo de disco y deja guardando los cambios.
 *
 * Devuelve cuando la caché de disco ya está en memoria. Quien espere esta promesa pinta con datos;
 * quien no, pinta vacío y se llena solo — nunca bloquea.
 */
export async function encenderCachePersistente(queryClient: QueryClient): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  let bd: Awaited<ReturnType<typeof abrir>>;
  try {
    bd = await abrir();
  } catch {
    return; // modo incógnito con almacenamiento bloqueado: el panel funciona igual, sin memoria
  }

  try {
    const guardado = (await bd.get(ALMACEN, CLAVE)) as Guardado | undefined;
    if (esUtilizable(guardado)) {
      hydrate(queryClient, guardado.estado);
    } else if (guardado) {
      await bd.delete(ALMACEN, CLAVE);
    }
  } catch {
    /* si lo guardado está corrupto, se ignora: peor sería no arrancar */
  }

  /*
   * 🪤 SE ESCRIBE CON RETARDO Y AGRUPANDO. La caché cambia en cada tecla del buscador y en cada
   *    revalidación; escribir en cada cambio pondría a IndexedDB a competir con la pantalla. Se
   *    junta un segundo de cambios y se guarda una vez.
   */
  let pendiente: ReturnType<typeof setTimeout> | null = null;
  const guardar = () => {
    if (pendiente) return;
    pendiente = setTimeout(() => {
      pendiente = null;
      try {
        const estado = dehydrate(queryClient, {
          shouldDehydrateQuery: (q) =>
            q.state.status === 'success' && PERSISTIBLES.includes(String(q.queryKey[0] ?? '')),
        });
        void bd.put(ALMACEN, { version: versionDeLaApp(), guardadoEn: Date.now(), estado }, CLAVE);
      } catch { /* la persistencia no rompe la app */ }
    }, ESPERA_ESCRITURA_MS);
  };

  queryClient.getQueryCache().subscribe(guardar);

  /*
   * 🚨 AL CERRAR SESIÓN SE BORRA TODO. Sin esto, los pedidos y los datos de contacto de los clientes
   *    de un negocio quedarían en el disco del navegador después de salir — y en una computadora
   *    compartida los leería el siguiente que entre. La persistencia sin esta línea no es una
   *    optimización, es una fuga.
   *
   * 🪤 SE ESCUCHA EL EVENTO EN VEZ DE LLAMAR AL `signOut`. `AuthContext` vive en el paquete
   *    compartido y no puede importar de `src`; acoplarlo al revés obligaría a que un paquete
   *    genérico sepa de la caché del panel. El evento ya existe y dice exactamente lo que pasó.
   */
  supabase.auth.onAuthStateChange((evento) => {
    if (evento === 'SIGNED_OUT') {
      if (pendiente) { clearTimeout(pendiente); pendiente = null; }
      queryClient.clear();
      void olvidarCachePersistida();
      /*
       * 🪤 Y LOS PERMISOS RECORDADOS TAMBIEN. `FeatureFlagsContext` guarda el plan y los modulos del
       *    negocio en localStorage para no tapar la pantalla en cada carga; si sobrevivieran al
       *    logout, el siguiente que entre en esa computadora veria el menu del negocio anterior
       *    antes de que su propia sesion lo corrija. Se limpia aca, con el resto, para que la
       *    salida sea UN solo lugar y no tres.
       */
      try {
        for (const clave of Object.keys(localStorage)) {
          if (clave.startsWith('permisos_del_panel_')) localStorage.removeItem(clave);
        }
      } catch { /* sin storage no hay nada que borrar */ }
    }
  });
  // Y una última al irse, para no perder el último minuto de trabajo por el retardo de arriba.
  window.addEventListener('pagehide', () => {
    if (pendiente) { clearTimeout(pendiente); pendiente = null; }
    try {
      const estado = dehydrate(queryClient, {
        shouldDehydrateQuery: (q) =>
          q.state.status === 'success' && PERSISTIBLES.includes(String(q.queryKey[0] ?? '')),
      });
      void bd.put(ALMACEN, { version: versionDeLaApp(), guardadoEn: Date.now(), estado }, CLAVE);
    } catch { /* nada */ }
  });
}
